import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../../components/Loader/Loader';
import { ReactComponent as Logo } from '../../assets/osavul-full.svg';
import styles from './ShareLoginPage.module.css';
import { useShareSentryTags } from '../../hooks/useShareSentryTags';

export function ShareLoginPage() {
  useShareSentryTags();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { reportId, narrativeId, dashboardId } = useParams();

  const hash = searchParams.get('h');
  const withPassword = searchParams.get('with_password');
  const [error, setError] = useState('');

  const copy = dashboardId
    ? {
        title: t('This dashboard is protected'),
        subtitle: t('Enter the password shared with you to access this dashboard'),
        submit: t('View dashboard'),
      }
    : {
        title: t('This case is protected'),
        subtitle: t('Enter the password shared with you to access this case'),
        submit: t('View case'),
      };

  const getShareToken = useCallback(async (hash, password) => {
    setError('');
    try {
      const resp = await fetch('/API/v1/reports/shares/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, hash }),
      });

      if (!resp.ok) {
        localStorage.removeItem('share_token');
        if (resp.status === 400) {
          setError(t('Incorrect password'));
        } else if ([401, 403, 404].includes(resp.status)) {
          setError(t('This link is invalid or has expired'));
        } else {
          setError(t('Something went wrong. Please try again.'));
        }
        return;
      }

      const data = await resp.json();
      localStorage.setItem('share_token', data.access_token);

      if (reportId) {
        navigate(`/reports/${reportId}/share?h=${hash}`);
      } else if (narrativeId) {
        navigate(`/narratives/${narrativeId}/share?h=${hash}`);
      } else if (dashboardId) {
        // keep with_password so an expired token can bounce straight back here
        // without showing the password form for a link that has none
        navigate(`/custom-dashboards/${dashboardId}/share?h=${hash}&with_password=${withPassword}`);
      }
    } catch {
      setError(t('Something went wrong. Please try again.'));
    }
  }, [navigate, reportId, narrativeId, dashboardId, withPassword, t]);

  useEffect(() => {
    if (withPassword === 'false') {
      getShareToken(hash, null);
    }
  }, [getShareToken, hash, withPassword]);

  async function handleSubmit(event) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const password = formData.get('password');
    getShareToken(hash, password);
  }

  if (withPassword === 'false' && !error) {
    return <Loader />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.card} role="main">
        <div className={styles.logo} aria-hidden="true">
          <Logo />
        </div>

        {withPassword === 'false' ? (
          <p className={styles.error} role="alert">{error}</p>
        ) : (
          <>
            <h1 className={styles.title}>{copy.title}</h1>
            <p className={styles.subtitle}>{copy.subtitle}</p>
            {error ? <p className={styles.error} role="alert">{error}</p> : null}

            <form onSubmit={handleSubmit} className={styles.form} noValidate>
              <div className={styles.field}>
                <input
                  id="share-password"
                  className={styles.input}
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder=" "
                  required
                  aria-required="true"
                />
                <label htmlFor="share-password" className={styles.label}>
                  {t('Password')}
                </label>
              </div>

              <button type="submit" className={styles.submit}>
                {copy.submit}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
